import { useTranslation } from 'react-i18next'
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
} from '@structural-climbing/ui'
import { RankBadge, AvatarPlaceholder } from './standingsUi'

/**
 * Only the fields the table actually renders. Declared structurally so both the
 * gym standings (which carry `gender`) and a competition scoreboard (which
 * carries `category` instead) can be passed without either having to adopt the
 * other's shape.
 */
export interface RankedEntry {
  userId: string
  displayName: string
  avatarExt: string | null
  avatarUpdatedAt: string | null
  totalScore: number
  routesClimbed: number
  flashesCount: number
  rank: number
}

interface Props {
  entries: RankedEntry[]
  myUserId: string | undefined
  apiUrl: string
  /**
   * The viewer's own row. Appended below the table, behind a dashed rule, when
   * they placed outside the entries shown — their score lands directly under
   * tenth place, so the climb to get there is a subtraction anyone can do.
   * Already among `entries`? Nothing is appended.
   */
  myEntry?: RankedEntry | null
  /** Overrides the default "no climbers yet" line. */
  emptyContent?: string
}

/**
 * The ranked table itself. Shared by the gym standings and the competition
 * scoreboard, which rank on the same numbers and should look identical.
 */
export function StandingsTable({ entries, myUserId, apiUrl, myEntry, emptyContent }: Props) {
  const { t } = useTranslation()

  const appendMine = Boolean(myEntry) && !entries.some((e) => e.userId === myEntry!.userId)
  const rows = appendMine ? [...entries, myEntry!] : entries

  return (
    /* table-fixed, and the two outer columns sized: an auto-layout table widens
       the member column to fit the longest name, which squeezed the score
       column until "9 routes · 7 flashes" wrapped over four lines and blew the
       row height apart. Fixed columns mean a long name truncates instead. */
    <Table aria-label={t('standings.topN')} removeWrapper classNames={{ table: 'table-fixed' }}>
      <TableHeader>
        <TableColumn className="w-12">{t('standings.colRank')}</TableColumn>
        <TableColumn>{t('standings.colMember')}</TableColumn>
        <TableColumn className="w-20 text-right">{t('standings.colScore')}</TableColumn>
      </TableHeader>
      <TableBody emptyContent={emptyContent ?? t('standings.empty')}>
        {rows.map((entry, index) => {
          const isMe = entry.userId === myUserId
          const isAppended = appendMine && index === rows.length - 1
          const avatarUrl =
            entry.avatarExt && entry.avatarUpdatedAt
              ? `${apiUrl}/member-avatar/${encodeURIComponent(entry.userId)}`
              : null

          return (
            <TableRow
              key={entry.userId}
              data-testid={isAppended ? 'my-standing-row' : undefined}
              className={[
                isMe ? 'you-row bg-primary/10 font-semibold' : '',
                isAppended ? 'border-t-2 border-dashed border-divider' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <TableCell className="align-top">
                <RankBadge rank={entry.rank} />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={entry.displayName}
                      className="h-7 w-7 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <AvatarPlaceholder name={entry.displayName} />
                  )}
                  {/* min-w-0 at every level, or the flex children refuse to
                      shrink below their content and truncate never fires. */}
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-baseline gap-1">
                      <span className="truncate text-sm" title={entry.displayName}>
                        {entry.displayName}
                      </span>
                      {isMe && (
                        <span className="shrink-0 text-xs text-foreground/40">
                          ({t('standings.you')})
                        </span>
                      )}
                    </div>
                    {/* Under the name, where it has the width to stay on one
                        line, rather than in the narrow score column. */}
                    <div className="truncate text-xs font-normal text-foreground/40">
                      {t('standings.routesCount', { count: entry.routesClimbed })}
                      {entry.flashesCount > 0 && (
                        <> · {t('standings.flashesCount', { count: entry.flashesCount })} ⚡</>
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="align-top">
                <div className="whitespace-nowrap text-right font-bold">
                  {entry.totalScore.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
